import { CurrencyPipe, DatePipe, TitleCasePipe } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ErrorHandler, NgModule, provideAppInitializer } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';

import { Router } from '@angular/router';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import {
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  provideAuth,
} from '@angular/fire/auth';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { provideFirestore, initializeFirestore } from '@angular/fire/firestore';
import { provideAnalytics, initializeAnalytics } from '@angular/fire/analytics';
import { GoogleMapsModule } from '@angular/google-maps';
import { IonicModule } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { createErrorHandler, TraceService } from '@sentry/angular';
import { of } from 'rxjs';
import { initializeFaro, getWebInstrumentations } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
/* internal dependencies */
import { IonicEquivalentModule } from '@smartflip/util-import-helper';
import { FeaturesAccountDeletionConfirmationModalModule } from '@smartflip/feat-account-deletion-modal';
import { StripePaymentModule, StripePaymentService } from '@smartflip/stripe';
import { FeaturesLoadingIndicatorModule } from '@smartflip/loading-indicator';
import { DataServicesDatafinitiModule } from '@smartflip/datafiniti';
import { ServicesHelperUtilsModule } from '@smartflip/helper-utils';
import { CustomCurrencyPipe, ToThousandsCurrencyPipe } from '@smartflip/pipes';
import { MenuComponent } from '@smart-flip/shared-components';
import { FormFieldsModule } from '@smartflip/form-fields';

import packageJson from './../../package.json';
import { AppRoutingModule } from './modules/app-routing.module';
import { AppComponent } from './app.component';
import { HomeComponent } from './views/home/home.component';

import { LenderFormComponent } from './views/lender/lender-form/lender-form.component';
import { PropertyDetailsModule } from './views/property-details/property-details.module';
import { SharedModule } from './shared/shared.module';
import { HUBSPOT_FUNCTIONS_BASE_URL } from '@smartflip/hubspot';
import { environment } from '../environments/environment';

const faroInitializer = () => {
  if (!Capacitor.isNativePlatform()) {
    return async () => {
      initializeFaro({
        url: environment.faro.url,
        app: {
          name: environment.faro.app.name,
          version: packageJson.version,
          environment: environment.faro.app.environment,
        },
        instrumentations: [
          // Mandatory, omits default instrumentations otherwise.
          ...getWebInstrumentations({ captureConsole: true }),
          // Tracing package to get end-to-end visibility for HTTP requests.
          new TracingInstrumentation(),
        ],
      });
    };
  } else {
    return () => of({});
  }
};

@NgModule({
  providers: [
    provideFirebaseApp(() =>
      initializeApp(environment.firebase, 'The Smart Flip'),
    ),
    provideFirestore(() =>
      initializeFirestore(getApp(), { ignoreUndefinedProperties: true }),
    ),
    provideAnalytics(() => initializeAnalytics(getApp())),
    provideAuth(() =>
      Capacitor.isNativePlatform()
        ? initializeAuth(getApp(), {
            persistence: indexedDBLocalPersistence,
          })
        : getAuth(),
    ),
    provideStorage(() => getStorage()), // only needed for storage features
    CurrencyPipe,
    DatePipe,
    TitleCasePipe,
    ToThousandsCurrencyPipe,
    ScreenOrientation,
    StripePaymentService,
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: true,
      }),
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    {
      provide: HUBSPOT_FUNCTIONS_BASE_URL,
      useValue: (environment as { useEmulator?: boolean }).useEmulator
        ? `http://localhost:5001/${environment.firebase.projectId}/us-central1`
        : environment.apiHostUrl,
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(faroInitializer()),
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    IonicEquivalentModule,
    ReactiveFormsModule,
    FormsModule,
    PropertyDetailsModule,
    SharedModule,
    DataServicesDatafinitiModule.forRoot(environment),
    ServicesHelperUtilsModule.forRoot(environment),
    StripePaymentModule.forRoot(environment),
    GoogleMapsModule,
    IonicModule.forRoot(),
    FeaturesLoadingIndicatorModule,
    FeaturesAccountDeletionConfirmationModalModule,
    FormFieldsModule,
    MenuComponent,
  ],
  declarations: [
    AppComponent,
    HomeComponent,
    CustomCurrencyPipe,
    LenderFormComponent,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
